import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
import { LANGUAGE_LABELS, SUPPORTED_LANGUAGES, type SupportedLanguage } from '@/i18n';
import { Dropdown } from './Dropdown';

export interface LanguageSwitcherProps {
  /** Extra classes for the trigger button. */
  className?: string;
  /**
   * Show the globe plus the two-letter language code instead of the full name
   * (for tight headers). Defaults to showing the name from `sm` up.
   */
  compact?: boolean;
}

/**
 * UI language selector: a globe trigger that opens the shipped languages, each
 * named in itself. The choice goes through `i18n.changeLanguage`, which the
 * language detector persists (localStorage) so it survives reloads and new
 * tabs. Composes {@link Dropdown}, so it inherits the menu's keyboard handling,
 * outside-click and portal positioning.
 */
export function LanguageSwitcher({ className = '', compact = false }: LanguageSwitcherProps) {
  const { t, i18n } = useTranslation();
  const current = (
    SUPPORTED_LANGUAGES.includes(i18n.resolvedLanguage as SupportedLanguage)
      ? i18n.resolvedLanguage
      : SUPPORTED_LANGUAGES[0]
  ) as SupportedLanguage;

  const items = SUPPORTED_LANGUAGES.map((code) => ({
    key: code,
    label: LANGUAGE_LABELS[code],
    checked: code === current,
    onSelect: () => {
      void i18n.changeLanguage(code);
    },
  }));

  return (
    <Dropdown
      label={t('common.language')}
      items={items}
      className={['gap-x-1.5 px-2.5 text-sm', className].filter(Boolean).join(' ')}
    >
      <Icon icon="heroicons:language" className="h-4 w-4 shrink-0" aria-hidden="true" />
      {compact ? (
        // The active language is always visible, even where there is no room
        // for its name: a globe alone says nothing about the current choice.
        <span className="text-xs font-semibold uppercase tracking-wide">{current}</span>
      ) : (
        <span className="hidden sm:inline">{LANGUAGE_LABELS[current]}</span>
      )}
    </Dropdown>
  );
}
